import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { formatInTimeZone } from "date-fns-tz";
import Button from "../../../../components/button/Button";
import TextInput from "../../../../components/inputs/textInput/TextInput";
import Loader from "../../../../components/loader/Loader";
import SettingsPagesContainer from "../../../../components/settingsPagesContainer/SettingsPagesContainer";
import TextWrapper from "../../../../components/textWrapper/TextWrapper";
import {
  fetchEmailLogsAPI,
  removeEmailSuppressionAPI,
} from "../../../../features/emailLogs/emailLogsAPI";
import { showModal } from "../../../../features/statusModal/statusModal";
import styles from "./emailDelivery.module.css";

// Support lookup for "I never got my code": every email the backend sent to
// an address in the last 30 days (verification / password-reset codes), what
// SES reported back, and whether the address is blocked after a hard bounce
// or a spam complaint — with a way to unblock it.

const PURPOSE_LABELS = {
  emailVerification: "رمز تأكيد البريد",
  passwordReset: "رمز إعادة تعيين كلمة المرور",
};

const STATUS = {
  sent: { label: "أُرسلت", tone: "neutral" },
  delivered: { label: "وصلت", tone: "success" },
  delayed: { label: "متأخرة", tone: "warning" },
  bounced: { label: "ارتدّت", tone: "danger" },
  complained: { label: "بلّغ عنها كإزعاج", tone: "danger" },
  rejected: { label: "رُفضت", tone: "danger" },
  failed: { label: "فشل الإرسال", tone: "danger" },
  suppressed: { label: "لم تُرسل (العنوان محظور)", tone: "danger" },
};

const EVENT_LABELS = {
  Delivery: "وصلت",
  DeliveryDelay: "تأخير",
  Bounce: "ارتداد",
  Complaint: "شكوى إزعاج",
  Reject: "رفض",
  "Rendering Failure": "خطأ في القالب",
};

const SUPPRESSION_REASONS = {
  bounce: "ارتداد دائم (العنوان غير موجود أو لا يستقبل البريد)",
  complaint: "شكوى إزعاج من صاحب البريد",
};

const EmailDelivery = () => {
  const dispatch = useDispatch();
  const { timeZone } = useSelector((state) => state.timeZone);
  const { rolePermissions } = useSelector((state) => state.user);
  const canUnsuppress = rolePermissions.includes("unsuppress-emails");
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | done
  const [result, setResult] = useState(null);
  const [unblocking, setUnblocking] = useState(false);

  const formatDate = (date) =>
    formatInTimeZone(new Date(date), timeZone || "Asia/Amman", "yyyy-MM-dd HH:mm");

  const search = async () => {
    const email = query.trim().toLowerCase();
    if (!email) return;
    setStatus("loading");
    try {
      const { data } = await fetchEmailLogsAPI(email);
      setResult(data?.data || null);
    } catch {
      setResult(null);
      dispatch(showModal({ message: "حصل خطأ اثناء جلب البيانات", status: "error" }));
    }
    setStatus("done");
  };

  const unblock = async () => {
    if (!result?.email || unblocking) return;
    setUnblocking(true);
    try {
      await removeEmailSuppressionAPI(result.email);
      setResult((prev) => ({ ...prev, suppression: null }));
      dispatch(showModal({ message: "تمت إزالة الحظر عن هذا العنوان", status: "success" }));
    } catch {
      dispatch(showModal({ message: "حصل خطأ اثناء إزالة الحظر", status: "error" }));
    }
    setUnblocking(false);
  };

  const logs = result?.logs || [];

  return (
    <SettingsPagesContainer title={"سجل البريد الإلكتروني"}>
      <div className={styles.searchRow}>
        <div
          onKeyDown={(e) => {
            if (e.key === "Enter") search();
          }}
        >
          <TextInput
            height="50px"
            width="400px"
            borderRadius="10px"
            placeHolder="البريد الإلكتروني للاعب"
            bgColor={"grey"}
            bordered={true}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <Button functionallity={search} size="midSize" variant="goldVariant">
          بحث
        </Button>
      </div>

      {status === "loading" && (
        <div className={styles.center}>
          <Loader />
        </div>
      )}

      {status === "done" && result && (
        <div className={styles.results}>
          {result.suppression && (
            <div className={styles.blocked}>
              <div>
                <TextWrapper textStyle="textMid">
                  هذا العنوان محظور من الإرسال
                </TextWrapper>
                <TextWrapper textStyle="textSmall">
                  السبب: {SUPPRESSION_REASONS[result.suppression.reason] || result.suppression.reason}
                  {result.suppression.detail ? ` — ${result.suppression.detail}` : ""}
                </TextWrapper>
                <TextWrapper textStyle="textXSmall">
                  منذ: {formatDate(result.suppression.lastEventAt || result.suppression.createdAt)}
                </TextWrapper>
              </div>
              {canUnsuppress && (
                <Button functionallity={unblock} size="midSize" variant="redVariant">
                  {unblocking ? "..." : "إزالة الحظر"}
                </Button>
              )}
            </div>
          )}

          {logs.length === 0 ? (
            <div className={styles.center}>
              <TextWrapper textStyle="textMid">
                لا توجد رسائل مرسلة لهذا العنوان خلال آخر 30 يوماً
              </TextWrapper>
            </div>
          ) : (
            logs.map((log) => {
              const s = STATUS[log.status] || { label: log.status, tone: "neutral" };
              return (
                <div key={log._id} className={styles.logRow}>
                  <div className={styles.logHeader}>
                    <TextWrapper textStyle="textMid">
                      {PURPOSE_LABELS[log.purpose] || log.purpose || "بريد"}
                    </TextWrapper>
                    <span className={`${styles.badge} ${styles[s.tone]}`}>{s.label}</span>
                  </div>
                  <TextWrapper textStyle="textXSmall">{formatDate(log.createdAt)}</TextWrapper>
                  {log.error && (
                    <TextWrapper textStyle="textXSmall">الخطأ: {log.error}</TextWrapper>
                  )}
                  {log.events?.length > 0 && (
                    <ul className={styles.events}>
                      {log.events.map((event, index) => (
                        <li key={`${event.type}-${index}`}>
                          <TextWrapper textStyle="textXSmall">
                            {formatDate(event.at)} — {EVENT_LABELS[event.type] || event.type}
                            {event.detail ? ` (${event.detail})` : ""}
                          </TextWrapper>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </SettingsPagesContainer>
  );
};

export default EmailDelivery;
